import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useReducedMotion,
} from 'framer-motion';
import { X, Send, ArrowRight, StopCircle } from 'lucide-react';
import { useMockPlan } from '../lib/mockPlan';
import { getSessionId } from '../lib/sessionManager';
import { getStaffingAnalytics } from '../lib/staffingApi';
import { streamAssistantResponse } from '../lib/tcAssistantStream';
import {
  detectIntent,
  runCannedFallback,
  getQuickChips,
  getWelcomeMessage,
  TOOLTIP_SUGGESTIONS,
  readStoredLang,
  writeStoredLang,
  tooltipsDisabled,
  disableTooltips,
  hasSeenGuidedTour,
  markGuidedTourSeen,
  getGuidedTourSteps,
  detectQuestionInjection,
} from '../lib/tcAssistantIntents';
import './TCRobotAvatar.css';

/**
 * TCRobotAvatar — Phase 12.
 *
 * Talent Connect-scoped avatar variant. Purple robot silhouette with digital
 * eyes and "TC" chest emblem. Replaces the consumer bioluminescent sphere on
 * /talent-connect/{root,recruiter,pricing}. Never mounts on /candidate*
 * (route dispatch happens in AgentAvatar.jsx).
 *
 * Behavioural additions on top of Phase 9 physics:
 *   1. Viewport constraint during drag (clamps within screen minus TopNav).
 *   2. Chat popover (canned intents + EN/ES toggle) instead of info popover.
 *   3. Proactive tooltip after 45s idle, rotates every 3 min, dismissible.
 *
 * Data fetched on-demand:
 *   - Analytics via /api/staffing/analytics (same endpoint AnalyticsCard uses)
 *   - Active room id from sessionStorage (written by RecruiterDashboard)
 *   - Plan/quota via useMockPlan()
 */

const HOME_MARGIN = 24;
const TOPNAV_HEIGHT = 56;
const MAGNETIC_RADIUS = 180;
const MAGNETIC_MAX_OFFSET = 20;
const TOOLTIP_FIRST_MS = 45_000;
const TOOLTIP_ROTATE_MS = 3 * 60 * 1000;

const ANALYTICS_POLL_MS = 45_000; // slightly staggered from AnalyticsCard's 30s poll

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
}

export default function TCRobotAvatar({ tone }) {
  const reduce = useReducedMotion();
  const { plan } = useMockPlan();

  // ------- refs + state -------
  const rootRef = useRef(null);
  const robotRef = useRef(null);

  const [chatOpen, setChatOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [hoverBubble, setHoverBubble] = useState(false);
  const [popoverFlip, setPopoverFlip] = useState({ vertical: 'top', horizontal: 'left' });

  // Magnetic pull motion values (small offset relative to fixed home)
  const magneticX = useMotionValue(0);
  const magneticY = useMotionValue(0);
  const smoothX = useSpring(magneticX, { stiffness: 140, damping: 18, mass: 0.6 });
  const smoothY = useSpring(magneticY, { stiffness: 140, damping: 18, mass: 0.6 });

  // Drag motion values (persistent after release — the robot stays where
  // dropped for the session).
  const dragX = useMotionValue(0);
  const dragY = useMotionValue(0);

  // Viewport-based drag constraints. Recomputed on resize + mount.
  const [constraints, setConstraints] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  useEffect(() => {
    const compute = () => {
      if (typeof window === 'undefined') return;
      const el = robotRef.current;
      const rW = el?.offsetWidth || 60;
      const rH = el?.offsetHeight || 82;
      // Home = bottom:HOME_MARGIN, right:HOME_MARGIN via CSS.
      // Delta x negative moves left; max = -(innerWidth - rW - HOME_MARGIN).
      // Delta x positive moves right toward the edge; keep 0 max (no overhang).
      // Delta y negative moves up; max = -(innerHeight - rH - HOME_MARGIN - TOPNAV_HEIGHT).
      setConstraints({
        left: -(window.innerWidth - rW - HOME_MARGIN * 2),
        right: 0,
        top: -(window.innerHeight - rH - HOME_MARGIN * 2 - TOPNAV_HEIGHT),
        bottom: 0,
      });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // ------- magnetic pointer tracking (identical to sphere) -------
  useEffect(() => {
    if (reduce) return undefined;
    let queued = false;
    let lastEvt = null;
    let hidden = document.hidden;
    const onVis = () => { hidden = document.hidden; };
    document.addEventListener('visibilitychange', onVis);

    const compute = () => {
      queued = false;
      if (!lastEvt || !robotRef.current || dragging || hidden) return;
      const rect = robotRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = lastEvt.clientX - cx;
      const dy = lastEvt.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const radius = isMobileViewport() ? 110 : MAGNETIC_RADIUS;
      if (dist > radius) {
        magneticX.set(0);
        magneticY.set(0);
        if (hoverBubble) setHoverBubble(false);
        return;
      }
      const strength = 1 - dist / radius;
      const tX = (dx / dist) * MAGNETIC_MAX_OFFSET * strength;
      const tY = (dy / dist) * MAGNETIC_MAX_OFFSET * strength;
      magneticX.set(Number.isFinite(tX) ? tX : 0);
      magneticY.set(Number.isFinite(tY) ? tY : 0);
      if (!hoverBubble) setHoverBubble(true);
    };

    const onMove = (e) => {
      lastEvt = e;
      if (queued) return;
      queued = true;
      requestAnimationFrame(compute);
    };
    document.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [reduce, dragging, hoverBubble, magneticX, magneticY]);

  // ------- click-outside + Escape -------
  useEffect(() => {
    if (!chatOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setChatOpen(false); };
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setChatOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [chatOpen]);

  // ------- drag handlers -------
  const handleDragStart = () => {
    setChatOpen(false);
    setDragging(true);
    magneticX.set(0);
    magneticY.set(0);
  };
  const handleDragEnd = () => {
    setDragging(false);
    updatePopoverFlip();
  };

  // ------- tap detection (framer's onTap fires only if pointer moved <3px).
  // Guarded so we only toggle when the tap landed on the robot itself, NOT
  // on the popover or tooltip — both live inside the same drag layer, so
  // otherwise every chat interaction would flip chatOpen and unmount the UI.
  //
  // framer-motion's onTap signature is (event, info); the event's .target
  // is the actual DOM node that was clicked.
  const handleTap = useCallback((event) => {
    const target = event?.target;
    if (target && robotRef.current && !robotRef.current.contains(target)) return;
    setChatOpen((o) => {
      if (!o) updatePopoverFlip();
      return !o;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleKey = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setChatOpen((o) => {
        if (!o) updatePopoverFlip();
        return !o;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- popover flip logic -------
  const updatePopoverFlip = useCallback(() => {
    if (!robotRef.current || typeof window === 'undefined') return;
    const rect = robotRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    // Grow away from viewport edges.
    setPopoverFlip({
      vertical: cy > window.innerHeight / 2 ? 'top' : 'bottom',
      horizontal: cx > window.innerWidth / 2 ? 'left' : 'right',
    });
  }, []);

  const driftPaused = dragging || hoverBubble || chatOpen || reduce;

  const dragProps = reduce
    ? {}
    : {
        drag: true,
        dragConstraints: constraints,
        dragMomentum: false,
        dragElastic: 0,
        onDragStart: handleDragStart,
        onDragEnd: handleDragEnd,
        whileDrag: { cursor: 'grabbing', scale: 1.05 },
      };

  const magneticStyle = reduce
    ? { x: dragX, y: dragY }
    : { x: smoothX, y: smoothY };

  return (
    <div
      className="tc-robot-root"
      ref={rootRef}
      data-testid="tc-robot-root"
      data-flip-vertical={popoverFlip.vertical}
      data-flip-horizontal={popoverFlip.horizontal}
    >
      {/* Drag layer (owns x/y for viewport position) */}
      <motion.div
        {...dragProps}
        onTap={handleTap}
        className="tc-robot-drag-layer"
        style={{ x: dragX, y: dragY, touchAction: reduce ? 'auto' : 'none' }}
        data-testid="tc-robot-drag"
      >
        {/* Magnetic layer (tiny offset) — the chat popover lives here so
            it follows any hover / drag transform automatically. */}
        <motion.div className="tc-robot-magnetic-layer" style={magneticStyle}>
          <div className={`tc-robot-drift-layer ${driftPaused ? 'is-paused' : ''}`}>
            <button
              ref={robotRef}
              type="button"
              className={`tc-robot-body tc-robot--${tone}`}
              aria-label="TC Assistant — click to open chat"
              aria-expanded={chatOpen}
              aria-haspopup="dialog"
              data-testid="tc-robot-avatar"
              data-tone={tone}
              onKeyDown={handleKey}
            >
              <RobotSvg />
            </button>
          </div>

          <AnimatePresence>
            {chatOpen && (
              <ChatPopover
                key="chat"
                flip={popoverFlip}
                plan={plan}
                onClose={() => setChatOpen(false)}
              />
            )}
          </AnimatePresence>

          {/* Proactive tooltip lives OUTSIDE chat (mutually exclusive with it) */}
          <ProactiveTooltip
            chatOpen={chatOpen}
            robotInteracting={dragging || hoverBubble}
            onOpenChat={() => { updatePopoverFlip(); setChatOpen(true); }}
            flip={popoverFlip}
          />
        </motion.div>
      </motion.div>
    </div>
  );
}

// ================================================================
// Robot silhouette (inline SVG — no PNG asset needed)
// ================================================================

function RobotSvg() {
  return (
    <svg
      viewBox="0 0 60 82"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="tc-robot-svg"
    >
      {/* Head */}
      <rect x="6" y="4" width="48" height="34" rx="10" className="tc-robot-head" />
      {/* Eyes (LED) */}
      <circle cx="22" cy="21" r="3.6" className="tc-robot-eye" />
      <circle cx="38" cy="21" r="3.6" className="tc-robot-eye" />
      {/* Neck */}
      <rect x="24" y="38" width="12" height="4" rx="2" className="tc-robot-neck" />
      {/* Body */}
      <rect x="3" y="42" width="54" height="36" rx="9" className="tc-robot-torso" />
      {/* Chest emblem — "TC" */}
      <text
        x="30" y="65"
        textAnchor="middle"
        className="tc-robot-emblem"
        fontFamily="Inter, system-ui, sans-serif"
        fontWeight="800"
        fontSize="14"
      >TC</text>
      {/* Bottom antenna / status dot */}
      <circle cx="30" cy="78" r="1.6" className="tc-robot-status-dot" />
    </svg>
  );
}

// ================================================================
// ChatPopover
// ================================================================

function ChatPopover({ flip, plan, onClose }) {
  const [lang, setLang] = useState(readStoredLang);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(false);
  // Phase 13 — messages now include an optional `streaming` flag while the
  // LLM is emitting tokens, and `fromFallback` when the response was served
  // from the canned intent matcher (LLM unreachable).
  const [messages, setMessages] = useState(() => [
    { id: 'welcome', role: 'assistant', text: getWelcomeMessage(readStoredLang()) },
  ]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);
  const abortRef = useRef(null);
  if (!sessionIdRef.current) sessionIdRef.current = getSessionId();

  // Fetch analytics for context injection (self-contained — doesn't depend on
  // the RecruiterDashboard being mounted). Refetch every 45s while chat open.
  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await getStaffingAnalytics(sessionIdRef.current, plan);
        if (!cancelled) {
          setAnalytics(res);
          setAnalyticsError(false);
        }
      } catch {
        if (!cancelled) setAnalyticsError(true);
      } finally {
        if (!cancelled) setAnalyticsLoading(false);
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, ANALYTICS_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [plan]);

  // Auto-scroll on new message
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // Autofocus input on open
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Phase 15 (ASSISTANT_GUIDED_TOUR, 3/4) — first-open onboarding tour.
  // Appends 3 canned messages sequentially (700ms stagger). Skipping the
  // tour cancels remaining timeouts and marks it seen immediately.
  // No LLM calls; messages have a `tourStep` flag so Skip can style step 1
  // with a "Skip tour" link inline in the bubble.
  const tourTimeoutsRef = useRef([]);
  const tourFinalizedRef = useRef(false);
  const finalizeTour = useCallback(() => {
    if (tourFinalizedRef.current) return;
    tourFinalizedRef.current = true;
    tourTimeoutsRef.current.forEach((id) => clearTimeout(id));
    tourTimeoutsRef.current = [];
    markGuidedTourSeen();
  }, []);
  const handleSkipTour = useCallback(() => {
    // Cancel pending steps + strip the skip-link flag from any tour messages
    // already rendered (step 1). Leave the message text intact.
    finalizeTour();
    setMessages((prev) => prev.map((m) => (m.tourStep ? { ...m, showSkip: false } : m)));
  }, [finalizeTour]);

  useEffect(() => {
    if (hasSeenGuidedTour()) return undefined;
    const steps = getGuidedTourSteps(readStoredLang());
    steps.forEach((text, i) => {
      const id = setTimeout(() => {
        setMessages((prev) => [
          ...prev,
          {
            id: `tour-${i}-${Date.now()}`,
            role: 'assistant',
            text,
            tourStep: i + 1,
            showSkip: i === 0,   // "Skip tour" link only on step 1
          },
        ]);
        if (i === steps.length - 1) finalizeTour();
      }, 700 * (i + 1));
      tourTimeoutsRef.current.push(id);
    });
    return () => {
      tourTimeoutsRef.current.forEach((tid) => clearTimeout(tid));
      tourTimeoutsRef.current = [];
    };
    // Run once on mount only — subsequent re-opens are guarded by
    // hasSeenGuidedTour() which flips true inside finalizeTour().
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ctx = useMemo(() => {
    const shortSid = (sessionIdRef.current || '').slice(0, 8);
    const activeRoomId =
      (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('talentconnect:active-room-id')) || null;
    return {
      totalInterviews: analytics?.total_interviews ?? null,
      qualificationPercentage: analytics?.qualification_percentage ?? null,
      dollarsSaved: analytics?.total_dollars_saved ?? null,
      hoursSaved: analytics?.total_hours_saved ?? null,
      plan: analytics?.plan || plan,
      activeRoomId,
      sessionId: shortSid,
      analyticsLoading,
      analyticsError,
    };
  }, [analytics, plan, analyticsLoading, analyticsError]);

  const handleSetLang = useCallback((next) => {
    setLang(next);
    writeStoredLang(next);
  }, []);

  // Phase 13 — streaming dispatcher.
  //
  // Flow:
  //   1. Push the user message + a placeholder assistant message (streaming=true).
  //   2. POST to /api/staffing/assistant/chat and read the SSE stream.
  //   3. Each chunk appends to the placeholder's text — React re-renders per token.
  //   4. On any error, replace the placeholder's text with the canned intent
  //      response and flag it `fromFallback` so the UI shows the "Offline mode"
  //      badge. Zero visible errors, zero broken states.
  //
  // AbortController is stored in abortRef so:
  //   - Submitting a new message aborts the previous stream in-flight.
  //   - Cleanup on ChatPopover unmount (chat closed) aborts too.
  //   - The user-visible Stop button hits abortRef.abort().
  const dispatchMessage = useCallback((rawText) => {
    // Phase 15 blocker fix — reorder guards so injection detection runs
    // BEFORE intent matching (`help` intent's keyword list includes `?`,
    // which was swallowing every question-ending injection), and so a
    // whitespace-only submit surfaces the "Please enter a valid question."
    // warning instead of silently returning.
    if (typeof rawText !== 'string' || rawText.length === 0) return;
    if (isStreaming) return;                 // guard: one stream at a time

    // 2. User pressed submit with only whitespace → render the warning
    //    (matches the empty-injection sub-case UX) and stop.
    if (rawText.trim().length === 0) {
      const errMsg = {
        id: `a-${Date.now() + 1}`,
        role: 'assistant',
        text: lang === 'es'
          ? '⚠️ Ingresá una pregunta válida.'
          : '⚠️ Please enter a valid question.',
        lang,
      };
      setMessages((prev) => [...prev, errMsg]);
      setInput('');
      return;
    }

    const trimmed = rawText.trim().slice(0, 800);

    // 3. Phase 15 (REALTIME_QUESTION_INJECTION, 4/4) — check injection FIRST.
    //    If it matches, we never fall through to detectIntent / LLM. This is
    //    the priority fix: `help` keywords contain '?' and would otherwise
    //    swallow any question-ending sentence before injection could fire.
    const inj = detectQuestionInjection(trimmed);
    if (inj.isInjection) {
      const userMsg = { id: `u-${Date.now()}`, role: 'user', text: trimmed, lang };
      if (inj.isEmpty) {
        const errMsg = {
          id: `a-${Date.now() + 1}`,
          role: 'assistant',
          text: lang === 'es'
            ? '⚠️ Ingresá una pregunta válida.'
            : '⚠️ Please enter a valid question.',
          lang,
        };
        setMessages((prev) => [...prev, userMsg, errMsg]);
        setInput('');
        return;
      }
      // Fire the injection event — RecruiterDashboard listens for this and
      // relays it to the active WS room. Confirmation reply is optimistic
      // (fire-and-forget); if there's no active room the recruiter still
      // sees the message but nothing lands on candidate side.
      try {
        window.dispatchEvent(new CustomEvent('tc:inject-question', {
          detail: { question: inj.question },
        }));
      } catch { /* noop */ }
      const okMsg = {
        id: `a-${Date.now() + 1}`,
        role: 'assistant',
        text: lang === 'es'
          ? `✅ Pregunta enviada al candidato:\n**${inj.question}**`
          : `✅ Question sent to candidate:\n**${inj.question}**`,
        lang,
      };
      setMessages((prev) => [...prev, userMsg, okMsg]);
      setInput('');
      return;
    }

    // 4. No injection → existing chip/LLM path unchanged.

    // Snapshot the state we'll need INSIDE async callbacks, before setState.
    const historyBeforeSend = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .filter((m) => m.id !== 'welcome')      // welcome is UI-only, not a real turn
      .slice(-6)                              // API max
      .map((m) => ({ role: m.role, content: m.text }));

    const userMsg = {
      id: `u-${Date.now()}`,
      role: 'user',
      text: trimmed,
      lang,
    };
    const assistantId = `a-${Date.now() + 1}`;
    const assistantMsg = {
      id: assistantId,
      role: 'assistant',
      text: '',
      lang,
      streaming: true,
    };
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
    setIsStreaming(true);

    // Abort any previous stream + open a fresh controller.
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    const controller = new AbortController();
    abortRef.current = controller;

    const payload = {
      message: trimmed,
      language: lang,
      recruiter_session_id: sessionIdRef.current,
      plan: ctxRef.current?.plan || plan,
      active_room_id: ctxRef.current?.activeRoomId || null,
      history: historyBeforeSend,
    };

    streamAssistantResponse(
      payload,
      // onChunk
      (chunk) => {
        setMessages((prev) => prev.map((m) => (
          m.id === assistantId ? { ...m, text: m.text + chunk } : m
        )));
      },
      // onDone
      (_meta) => {
        setMessages((prev) => prev.map((m) => (
          m.id === assistantId ? { ...m, streaming: false } : m
        )));
        setIsStreaming(false);
      },
      // onError → canned fallback
      (_err) => {
        const cannedText = runCannedFallback(trimmed, ctxRef.current, lang);
        const cannedIntent = detectIntent(trimmed);
        setMessages((prev) => prev.map((m) => (
          m.id === assistantId
            ? {
                ...m,
                text: cannedText,
                streaming: false,
                fromFallback: true,
                intent: cannedIntent,
              }
            : m
        )));
        setIsStreaming(false);
      },
      controller.signal,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, lang, plan, isStreaming]);

  // Stop button — abort the in-flight stream. Whatever text arrived stays;
  // the placeholder just flips `streaming: false` and drops the cursor.
  const handleStop = useCallback(() => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    setMessages((prev) => prev.map((m) => (
      m.streaming ? { ...m, streaming: false, canceled: true } : m
    )));
    setIsStreaming(false);
  }, []);

  // Keep the latest ctx in a ref so dispatchMessage's async callbacks read
  // fresh values without re-creating the callback on every analytics tick.
  const ctxRef = useRef(null);
  ctxRef.current = ctx;

  // Cleanup any in-flight stream when the popover unmounts (user closed chat).
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch { /* noop */ }
      }
    };
  }, []);

  const chips = useMemo(() => getQuickChips(lang), [lang]);

  const placeholder = lang === 'es' ? 'Escribí un mensaje…' : 'Type a message…';
  const disclaimer = lang === 'es'
    ? 'Asistente en beta · Claude Sonnet 4.5'
    : 'Beta assistant · Claude Sonnet 4.5';

  return (
    <motion.div
      className={`tc-chat-popover tc-chat-popover--v-${flip.vertical} tc-chat-popover--h-${flip.horizontal}`}
      data-testid="tc-chat-popover"
      role="dialog"
      aria-label="TC Assistant chat"
      initial={{ opacity: 0, scale: 0.94, y: flip.vertical === 'top' ? 8 : -8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: 4 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="tc-chat-header">
        <span className="tc-chat-header-brand">
          <span className="tc-chat-header-mini" aria-hidden="true">
            <svg viewBox="0 0 60 82" width="20" height="26"><RobotSvg /></svg>
          </span>
          <span className="tc-chat-header-title">TC Assistant</span>
        </span>
        <div className="tc-chat-header-right">
          <div className="tc-chat-lang-toggle" role="group" aria-label="Language">
            <button
              type="button"
              className={`tc-chat-lang-btn ${lang === 'en' ? 'tc-chat-lang-btn--active' : ''}`}
              onClick={() => handleSetLang('en')}
              data-testid="tc-chat-lang-en"
              aria-pressed={lang === 'en'}
            >EN</button>
            <button
              type="button"
              className={`tc-chat-lang-btn ${lang === 'es' ? 'tc-chat-lang-btn--active' : ''}`}
              onClick={() => handleSetLang('es')}
              data-testid="tc-chat-lang-es"
              aria-pressed={lang === 'es'}
            >ES</button>
          </div>
          <button
            type="button"
            className="tc-chat-close"
            aria-label={lang === 'es' ? 'Cerrar chat' : 'Close chat'}
            onClick={onClose}
            data-testid="tc-chat-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="tc-chat-body" ref={listRef} data-testid="tc-chat-body">
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} onSkipTour={handleSkipTour} />
        ))}
      </div>

      {/* Quick chips */}
      <div className="tc-chat-chips" data-testid="tc-chat-chips">
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            className="tc-chat-chip"
            onClick={() => dispatchMessage(c.text)}
            disabled={isStreaming}
            data-testid={`tc-chat-chip-${c.id}`}
          >{c.label}</button>
        ))}
      </div>

      {/* Input */}
      <form
        className="tc-chat-input-row"
        onSubmit={(e) => { e.preventDefault(); dispatchMessage(input); }}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          className="tc-chat-input"
          data-testid="tc-chat-input"
          maxLength={800}
          disabled={isStreaming}
        />
        {isStreaming ? (
          <button
            type="button"
            className="tc-chat-send tc-chat-send--stop"
            aria-label={lang === 'es' ? 'Detener respuesta' : 'Stop response'}
            onClick={handleStop}
            data-testid="tc-chat-stop"
          ><StopCircle size={16} /></button>
        ) : (
          <button
            type="submit"
            className="tc-chat-send"
            aria-label={lang === 'es' ? 'Enviar' : 'Send'}
            disabled={!input.trim()}
            data-testid="tc-chat-send"
          ><Send size={16} /></button>
        )}
      </form>

      <p className="tc-chat-disclaimer">{disclaimer}</p>
    </motion.div>
  );
}

function MessageBubble({ message, onSkipTour }) {
  // Phase 13 — role is now 'assistant' (previously 'bot'). Kept back-compat
  // for the 'welcome' seed message which still has role 'assistant' too.
  const isBot = message.role === 'assistant';
  const html = useMemo(() => renderMinimalMd(message.text), [message.text]);

  // Special affordance: upgrade intent bot messages get an inline "Go to Pricing" link.
  // For LLM responses the intent field isn't set, so we detect "pricing" keyword
  // in the message text as a soft trigger (only for canned fallback messages).
  const showUpgradeLink = isBot && message.fromFallback && message.intent === 'upgrade';
  const linkLabel = message.lang === 'es' ? 'Ir a Pricing' : 'Go to Pricing';

  // Streaming states.
  const isEmptyStreaming = isBot && message.streaming && !message.text;
  const isTypingStream   = isBot && message.streaming && !!message.text;
  const wasCanceled      = isBot && message.canceled;

  const fallbackLabel = message.lang === 'es'
    ? '⚠️ Modo offline — respuesta cacheada'
    : '⚠️ Offline mode — showing cached response';
  const canceledLabel = message.lang === 'es' ? '· Respuesta cancelada' : '· Response canceled';
  const skipLabel = message.lang === 'es' ? 'Saltar tour' : 'Skip tour';

  return (
    <div
      className={`tc-chat-msg tc-chat-msg--${isBot ? 'bot' : 'user'}`}
      data-testid={`tc-chat-msg-${isBot ? 'assistant' : 'user'}${message.tourStep ? `-tour-${message.tourStep}` : ''}`}
    >
      {isBot && (
        <span className="tc-chat-msg-avatar" aria-hidden="true">
          <svg viewBox="0 0 60 82" width="18" height="22"><RobotSvg /></svg>
        </span>
      )}
      <div className="tc-chat-msg-bubble">
        {isEmptyStreaming ? (
          <span
            className="tc-chat-typing"
            aria-label="Assistant is thinking"
            data-testid="tc-chat-typing"
          >
            <span /><span /><span />
          </span>
        ) : (
          <>
            <div dangerouslySetInnerHTML={{ __html: html }} />
            {isTypingStream && (
              <span className="tc-chat-cursor" aria-hidden="true" data-testid="tc-chat-cursor" />
            )}
          </>
        )}
        {showUpgradeLink && (
          <Link
            to="/talent-connect/pricing"
            className="tc-chat-inline-link"
            data-testid="tc-chat-inline-pricing"
          >
            {linkLabel} <ArrowRight size={12} />
          </Link>
        )}
        {message.showSkip && onSkipTour && (
          <button
            type="button"
            className="tc-chat-inline-link"
            onClick={onSkipTour}
            data-testid="tc-chat-skip-tour"
          >
            {skipLabel} <ArrowRight size={12} />
          </button>
        )}
        {message.fromFallback && !message.streaming && (
          <span className="tc-chat-fallback-badge" data-testid="tc-chat-fallback-badge">
            {fallbackLabel}
          </span>
        )}
        {wasCanceled && (
          <span className="tc-chat-fallback-badge tc-chat-fallback-badge--canceled">
            {canceledLabel}
          </span>
        )}
      </div>
    </div>
  );
}

/** Minimal markdown: **bold**, newlines. Escapes HTML first. */
function renderMinimalMd(text) {
  const escaped = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br />');
}

// ================================================================
// Proactive tooltip
// ================================================================

function ProactiveTooltip({ chatOpen, robotInteracting, onOpenChat, flip }) {
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const [disabled, setDisabled] = useState(tooltipsDisabled);
  const [lang, setLang] = useState(readStoredLang);

  // Keep tooltip lang in sync with chat lang toggle (both read same key).
  useEffect(() => {
    const reread = () => setLang(readStoredLang());
    window.addEventListener('storage', reread);
    return () => window.removeEventListener('storage', reread);
  }, []);

  // Also re-read lang whenever the tooltip toggles visible — cheap and covers
  // same-tab updates by the chat's [EN|ES] toggle.
  useEffect(() => {
    if (visible) setLang(readStoredLang());
  }, [visible]);

  // Timer management. Any interaction (chat open OR user activity in page)
  // resets timers. Uses page-level activity events (mousemove / keydown / touchstart).
  useEffect(() => {
    if (disabled) return undefined;

    let firstTimer = null;
    let rotateTimer = null;
    let lastActivity = Date.now();

    const scheduleFirst = () => {
      clearTimeout(firstTimer);
      firstTimer = setTimeout(() => {
        if (!chatOpen && Date.now() - lastActivity >= TOOLTIP_FIRST_MS - 500) {
          setVisible(true);
          scheduleRotate();
        }
      }, TOOLTIP_FIRST_MS);
    };
    const scheduleRotate = () => {
      clearTimeout(rotateTimer);
      rotateTimer = setTimeout(() => {
        setIndex((i) => (i + 1) % TOOLTIP_SUGGESTIONS.en.length);
        scheduleRotate();
      }, TOOLTIP_ROTATE_MS);
    };
    const bumpActivity = () => {
      lastActivity = Date.now();
      if (visible) setVisible(false);
      clearTimeout(rotateTimer);
      scheduleFirst();
    };

    // Kick off
    scheduleFirst();
    document.addEventListener('pointerdown', bumpActivity, { passive: true });
    document.addEventListener('keydown', bumpActivity);

    return () => {
      clearTimeout(firstTimer);
      clearTimeout(rotateTimer);
      document.removeEventListener('pointerdown', bumpActivity);
      document.removeEventListener('keydown', bumpActivity);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, chatOpen]);

  // Interaction (drag or hover magnetic bubble) hides tooltip immediately
  useEffect(() => {
    if (robotInteracting && visible) setVisible(false);
  }, [robotInteracting, visible]);

  if (disabled) return null;
  const suggestions = TOOLTIP_SUGGESTIONS[lang] || TOOLTIP_SUGGESTIONS.en;
  const message = suggestions[index % suggestions.length];

  const dismissLabel = lang === 'es' ? 'No mostrar sugerencias' : "Don't show suggestions";
  const dismissAria = lang === 'es' ? 'Cerrar sugerencia' : 'Dismiss suggestion';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`tc-tooltip tc-tooltip--v-${flip.vertical} tc-tooltip--h-${flip.horizontal}`}
          role="status"
          aria-live="polite"
          data-testid="tc-robot-tooltip"
          initial={{ opacity: 0, y: 8, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 4, scale: 0.96 }}
          transition={{ duration: 0.24 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="tc-tooltip-message"
            onClick={() => { setVisible(false); onOpenChat(); }}
            data-testid="tc-tooltip-open-chat"
          >
            {message}
          </button>
          <button
            type="button"
            className="tc-tooltip-close"
            aria-label={dismissAria}
            onClick={() => setVisible(false)}
            data-testid="tc-tooltip-close"
          >
            <X size={12} />
          </button>
          <label className="tc-tooltip-dontshow">
            <input
              type="checkbox"
              onChange={(e) => {
                if (e.target.checked) {
                  disableTooltips();
                  setDisabled(true);
                  setVisible(false);
                }
              }}
              data-testid="tc-tooltip-dontshow"
            />
            <span>{dismissLabel}</span>
          </label>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
